import { createContext, PropsWithChildren, useContext } from 'react';
import { scaleLinear } from 'd3-scale';
import { zoomIdentity, ZoomTransform } from 'd3-zoom';
import { ZoomContext } from './d3Zoomable';
import { HotSpot, HotSpotItem, useDataContext } from './dataContext';

const xScale = scaleLinear();
const yScale = scaleLinear();

type ScaledHotSpot<T extends HotSpotItem> = HotSpot<T> & {
   scaledX: number;
   scaledY: number;
};

function getScaledData<T extends HotSpotItem>(
   transform: ZoomTransform,
   data: HotSpot<T>[]
): ScaledHotSpot<T>[] {
   const scaledXFunc = transform.rescaleX(xScale);
   const scaledYFunc = transform.rescaleY(yScale);
   // TODO: This cast is to keep the mutation. We should probably just return objects.
   // Keeping the mutation for now since we are only adding types
   return (data as ScaledHotSpot<T>[]).map(datapoint => {
      datapoint.scaledX = scaledXFunc(datapoint.x);
      datapoint.scaledY = scaledYFunc(datapoint.y);
      return datapoint;
   });
}

const scaledDataDefaults = {
   data: [] as ScaledHotSpot<HotSpotItem>[],
};

const ScaledDataContext = createContext(scaledDataDefaults);

type ScaledDataContextType<T extends HotSpotItem> = {
   data: ScaledHotSpot<T>[];
};

export function useScaledDataContext<T extends HotSpotItem>() {
   const context = useContext(ScaledDataContext);
   return context as ScaledDataContextType<T>;
}

/* eslint-disable-next-line @typescript-eslint/no-empty-object-type */
export function ScaledDataProvider<T extends HotSpotItem>({ children }: PropsWithChildren<{}>) {
   const dataContext = useDataContext<T>();
   const data = dataContext.data;
   const zoomContext = useContext(ZoomContext);
   const transform =
      zoomContext.d3Transform || zoomIdentity.scale(zoomContext.getInitialZoomFactor());
   const scaledData = getScaledData(transform, data);
   const scaledDataContext = {
      data: scaledData,
   };

   return (
      <ScaledDataContext.Provider value={scaledDataContext}>{children}</ScaledDataContext.Provider>
   );
}
